import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtEur } from "@/components/ui-kit";
import { TIPI_SOGGETTO } from "@/lib/margineCommessa";

export function useNomiSoggetti() {
  const [nomi, setNomi] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/subappaltatori").catch(() => ({ data: [] })), api.get("/fornitori-listini").catch(() => ({ data: [] }))])
      .then(([a, b]) => setNomi([...new Set([...(a.data || []).map((x) => x.nome), ...(b.data || []).map((x) => x.fornitore_nome || x.fornitore || x.nome)].filter(Boolean))]));
  }, []);
  return nomi;
}

// Affida una o più voci del computo a un soggetto, con prezzo voce per voce o un totale unico ripartito.
export function AffidaDialog({ open, onOpenChange, cid, items, onDone }) {
  const nomi = useNomiSoggetti();
  const [tipo, setTipo] = useState("artigiano");
  const [nome, setNome] = useState("");
  const [modo, setModo] = useState("per_voce");
  const [prezzi, setPrezzi] = useState({});
  const [totale, setTotale] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setPrezzi(Object.fromEntries(items.map((it) => [it.id, it.affidamento?.costo_effettivo ?? ""])));
    setTotale("");
  }, [open, items]);
  const prevTot = items.reduce((s, it) => s + (Number(it.costo_previsto) || 0), 0);
  const save = async () => {
    if (modo === "per_voce" && items.some((it) => prezzi[it.id] === "" || prezzi[it.id] == null)) return toast.error("Inserisci il prezzo di ogni voce (o scegli 'prezzo totale')");
    if (modo === "totale" && !(Number(totale) > 0)) return toast.error("Inserisci il prezzo totale");
    setBusy(true);
    try {
      await api.post(`/commesse/${cid}/workflow/affida`, {
        item_ids: items.map((i) => i.id), tipo, nome, modo,
        prezzi: Object.fromEntries(Object.entries(prezzi).filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v)])),
        totale: Number(totale) || null,
      });
      toast.success(`${items.length} voci affidate`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="affida-dialog">
        <DialogHeader>
          <DialogTitle>Affida {items.length} voci</DialogTitle>
          <DialogDescription>Costo previsto (prezzi d'acquisto): <b>{fmtEur(prevTot)}</b>. Il costo che inserisci aggiorna subito il margine della commessa.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>A chi</Label>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="w-full h-9 border border-zinc-300 rounded px-2 text-sm" data-testid="affida-tipo">
              {TIPI_SOGGETTO.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div><Label>Nome</Label>
            <Input value={nome} onChange={(e) => setNome(e.target.value)} list="affida-nomi" placeholder={tipo === "interno" ? "Operai interni" : "Ditta / professionista"} data-testid="affida-nome" />
            <datalist id="affida-nomi">{nomi.map((n) => <option key={n} value={n} />)}</datalist>
          </div>
        </div>
        <div className="flex gap-2 text-xs">
          {[["per_voce", "Prezzo voce per voce"], ["totale", "Un prezzo totale"], ["previsto", "Senza prezzo (resta il previsto)"]].map(([k, l]) => (
            <button key={k} onClick={() => setModo(k)} className={`px-3 py-1.5 rounded border ${modo === k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300"}`} data-testid={`affida-modo-${k}`}>{l}</button>
          ))}
        </div>
        {modo === "totale" && (
          <div><Label>Prezzo totale concordato (€)</Label>
            <Input type="number" value={totale} onChange={(e) => setTotale(e.target.value)} data-testid="affida-totale" />
            <p className="text-[11px] text-zinc-500 mt-1">Viene ripartito sulle voci in proporzione al costo previsto.</p>
          </div>
        )}
        <div className="max-h-72 overflow-auto border rounded">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 text-[10px] uppercase text-zinc-500"><tr><th className="text-left px-2 py-1.5">Voce</th><th className="text-right px-2">Q.tà</th><th className="text-right px-2">Previsto</th>{modo === "per_voce" && <th className="text-right px-2 w-32">Prezzo concordato</th>}</tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t">
                  <td className="px-2 py-1.5">{it.name}</td>
                  <td className="px-2 text-right mono">{it.qty} {it.unit}</td>
                  <td className="px-2 text-right mono">{fmtEur(it.costo_previsto)}</td>
                  {modo === "per_voce" && <td className="px-2 py-1"><Input type="number" value={prezzi[it.id] ?? ""} onChange={(e) => setPrezzi({ ...prezzi, [it.id]: e.target.value })} className="h-7 text-xs text-right" data-testid={`affida-prezzo-${it.id}`} /></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} style={{ background: "var(--brand)", color: "white" }} data-testid="affida-save">Affida</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
